function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }

function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }

function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }

function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }

function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == _typeof(i) ? i : i + ""; }

function _toPrimitive(t, r) { if ("object" != _typeof(t) || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != _typeof(i)) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }

import { clearInterval, setInterval } from '../../tools/timer';
import { Observable } from '../../tools/observable';
import { ONE_SECOND, dateNow } from '../../tools/utils/timeUtils';
import { throttle } from '../../tools/utils/functionUtils';
import { generateUUID } from '../../tools/utils/stringUtils';
import { display } from '../../tools/display';
import { selectCookieStrategy, initCookieStrategy } from './storeStrategies/sessionInCookie';
import { getExpiredSessionState, isSessionInExpiredState, isSessionInNotStartedState, isSessionStarted } from './sessionState';
import { initLocalStorageStrategy, selectLocalStorageStrategy } from './storeStrategies/sessionInLocalStorage';
import { processSessionStoreOperations } from './sessionStoreOperations';
import { SESSION_NOT_TRACKED, SessionPersistence } from './sessionConstants';
/**
 * Every second, the storage will be polled to check for any change that can occur
 * to the session state in another browser tab, or another window.
 * This value has been determined from our previous cookie-only implementation.
 */

export var STORAGE_POLL_DELAY = ONE_SECOND;
/**
 * Selects the correct session store strategy type based on the configuration and storage
 * availability.
 */

export function selectSessionStoreStrategyType(initConfiguration) {
  switch (initConfiguration.sessionPersistence) {
    case SessionPersistence.COOKIE:
      return selectCookieStrategy(initConfiguration);

    case SessionPersistence.LOCAL_STORAGE:
      return selectLocalStorageStrategy();

    case undefined:
      {
        var sessionStoreStrategyType = selectCookieStrategy(initConfiguration);

        if (!sessionStoreStrategyType && initConfiguration.allowFallbackToLocalStorage) {
          sessionStoreStrategyType = selectLocalStorageStrategy();
        }

        return sessionStoreStrategyType;
      }

    default:
      display.error("Invalid session persistence '".concat(String(initConfiguration.sessionPersistence), "'"));
  }
}
export function getSessionStoreStrategy(sessionStoreStrategyType, configuration) {
  return sessionStoreStrategyType.type === SessionPersistence.COOKIE ? initCookieStrategy(configuration, sessionStoreStrategyType.cookieOptions) : initLocalStorageStrategy(configuration);
}
/**
 * Different session concepts:
 * - tracked, the session has an id and is updated along the user navigation
 * - not tracked, the session does not have an id but it is updated along the user navigation
 * - inactive, no session in store or session expired, waiting for a renew session
 */

export function startSessionStore(sessionStoreStrategyType, configuration, productKey, computeTrackingType) {
  var sessionStoreStrategy = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : getSessionStoreStrategy(sessionStoreStrategyType, configuration);
  var renewObservable = new Observable();
  var expireObservable = new Observable();
  var sessionStateUpdateObservable = new Observable();
  var watchSessionTimeoutId = setInterval(watchSession, STORAGE_POLL_DELAY);
  var sessionCache;
  startSession();

  var _throttle = throttle(function () {
    processSessionStoreOperations({
      process: function process(sessionState) {
        if (isSessionInNotStartedState(sessionState)) {
          return;
        }

        var synchronizedSession = synchronizeSession(sessionState);
        expandOrRenewSessionState(synchronizedSession);
        return synchronizedSession;
      },
      after: function after(sessionState) {
        if (isSessionStarted(sessionState) && !hasSessionInCache()) {
          renewSessionInCache(sessionState);
        }

        sessionCache = sessionState;
      }
    }, sessionStoreStrategy);
  }, STORAGE_POLL_DELAY),
      throttledExpandOrRenewSession = _throttle.throttled,
      cancelExpandOrRenewSession = _throttle.cancel;

  function expandSession() {
    processSessionStoreOperations({
      process: function process(sessionState) {
        return hasSessionInCache() ? synchronizeSession(sessionState) : undefined;
      }
    }, sessionStoreStrategy);
  }
  /**
   * allows two behaviors:
   * - if the session is active, synchronize the session cache without updating the session store
   * - if the session is not active, clear the session store and expire the session cache
   */


  function watchSession() {
    var sessionState = sessionStoreStrategy.retrieveSession();

    if (isSessionInExpiredState(sessionState)) {
      processSessionStoreOperations({
        process: function process(sessionState) {
          return isSessionInExpiredState(sessionState) ? getExpiredSessionState(sessionState, configuration) : undefined;
        },
        after: synchronizeSession
      }, sessionStoreStrategy);
    } else {
      synchronizeSession(sessionState);
    }
  }

  function synchronizeSession(sessionState) {
    if (isSessionInExpiredState(sessionState)) {
      sessionState = getExpiredSessionState(sessionState, configuration);
    }

    if (hasSessionInCache()) {
      if (isSessionInCacheOutdated(sessionState)) {
        expireSessionInCache();
      } else {
        sessionStateUpdateObservable.notify({
          previousState: sessionCache,
          newState: sessionState
        });
        sessionCache = sessionState;
      }
    }

    return sessionState;
  }

  function startSession() {
    processSessionStoreOperations({
      process: function process(sessionState) {
        if (isSessionInNotStartedState(sessionState)) {
          sessionState.anonymousId = generateUUID();
          return getExpiredSessionState(sessionState, configuration);
        }
      },
      after: function after(sessionState) {
        sessionCache = sessionState;
      }
    }, sessionStoreStrategy);
  }

  function expandOrRenewSessionState(sessionState) {
    if (isSessionInNotStartedState(sessionState)) {
      return false;
    }

    var trackingType = computeTrackingType(sessionState[productKey]);
    sessionState[productKey] = trackingType;
    delete sessionState.isExpired;

    if (trackingType !== SESSION_NOT_TRACKED && !sessionState.id) {
      sessionState.id = generateUUID();
      sessionState.created = String(dateNow());
    }
  }

  function hasSessionInCache() {
    return (sessionCache === null || sessionCache === void 0 ? void 0 : sessionCache[productKey]) !== undefined;
  }

  function isSessionInCacheOutdated(sessionState) {
    return sessionCache.id !== sessionState.id || sessionCache[productKey] !== sessionState[productKey];
  }

  function expireSessionInCache() {
    sessionCache = getExpiredSessionState(sessionCache, configuration);
    expireObservable.notify();
  }

  function renewSessionInCache(sessionState) {
    sessionCache = sessionState;
    renewObservable.notify();
  }

  function updateSessionState(partialSessionState) {
    processSessionStoreOperations({
      process: function process(sessionState) {
        return _objectSpread(_objectSpread({}, sessionState), partialSessionState);
      },
      after: synchronizeSession
    }, sessionStoreStrategy);
  }

  return {
    expandOrRenewSession: throttledExpandOrRenewSession,
    expandSession: expandSession,
    getSession: function getSession() {
      return sessionCache;
    },
    renewObservable: renewObservable,
    expireObservable: expireObservable,
    sessionStateUpdateObservable: sessionStateUpdateObservable,
    restartSession: startSession,
    expire: function expire(hasConsent) {
      cancelExpandOrRenewSession();

      if (hasConsent === false && sessionCache) {
        delete sessionCache.anonymousId;
      }

      sessionStoreStrategy.expireSession(sessionCache);
      synchronizeSession(getExpiredSessionState(sessionCache, configuration));
    },
    stop: function stop() {
      clearInterval(watchSessionTimeoutId);
    },
    updateSessionState: updateSessionState
  };
}